/*
 * Mining profitability of ASIC miners (and GPUs on the home page), drawn in the browser.
 * Data: JSON published every hour by the Prefect flows "ASIC Mining Profitability" and "GPU Rental Profitability"
 * (gpu.happymining.eu, URLs in window.hmRent from snippets/hm-rent-config.liquid). For an ASIC the revenue is
 * hashrate x EUR per H/s per day of its algorithm; electricity = power x 24 h x price per kWh.
 * Three places:
 *   - product cards (.hm-card-specs from snippets/hm-card-specs.liquid): one line « ≈ x €/day net »;
 *   - ASIC product page (sections/hm-asic-rent.liquid, [data-hm-asic]): figures with electricity price and
 *     number of machines, payback on the product price (tiles drawn by the section, values filled here);
 *   - home page (sections/hm-rent-home.liquid, [data-hm-rent-home]): best ASICs of a collection + best GPUs;
 *   - home miners (snippets/hm-home-miner-product.liquid, sections/hm-home-miner-guide.liquid, their product cards in
 *     snippets/hm-card-specs.liquid): no revenue, only the chance of finding a Bitcoin block in solo mining
 *     ([data-hm-odds]) and the block reward in euros, from the live network figures (btc-network.json, every 10 min).
 * Global electricity price (window.hmKwh): the visitor sets his price once (pop-up opened by [data-hm-kwh-edit],
 * or any calculator field); it is stored in localStorage (« hm_kwh ») and every estimate listens to « hm:kwh ».
 * Bitcoin price (window.hmBtc, SHA-256 calculator of the product page only): Bitcoin's all-time high by default
 * (asic.json btc_eur_ath); a price typed by the visitor is kept in localStorage (« hm_btc », also read by the
 * calculator pages, sections/hm-btc-calc.liquid) until he picks « ATH » again. The SHA-256 revenue is scaled by
 * price / current price (same network difficulty). Cards and the home page keep the current price.
 */
(function () {
  'use strict';
  var cfg = window.hmRent || {};
  var i18n = cfg.i18n || {};
  // Shopify's t filter returns HTML-escaped text (d&#39;occupation): decode once, the strings are escaped on output
  (function () {
    var dec = document.createElement('textarea');
    Object.keys(i18n).forEach(function (k) { dec.innerHTML = i18n[k]; i18n[k] = dec.value; });
  })();
  var locale = cfg.locale || document.documentElement.lang || 'fr';
  var DAYS_PER_MONTH = 30.4;
  var UNITS = { T: 1e12, G: 1e9, M: 1e6, K: 1e3 };
  var cache = {};

  function load(url) {
    if (!url) return Promise.reject(new Error('no url'));
    if (!cache[url]) {
      cache[url] = fetch(url, { mode: 'cors', credentials: 'omit' }).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });
    }
    return cache[url];
  }
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }
  function fmt(digits) { return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', minimumFractionDigits: digits, maximumFractionDigits: digits }); }
  var eur0 = fmt(0), eur2 = fmt(2);
  function money(v, f) { return '<span class="' + (v < 0 ? 'hm-rent-neg' : 'hm-rent-pos') + '">' + (f || eur0).format(v) + '</span>'; }
  function t(key, vars) {
    var s = i18n[key] || '';
    Object.keys(vars || {}).forEach(function (k) { s = s.replace('{' + k + '}', vars[k]); });
    return s;
  }

  // hashrate "473" + "TH/s" -> H/s
  function hs(hash, unit) {
    var f = UNITS[String(unit || '').trim().charAt(0).toUpperCase()] || 1;
    return (parseFloat(String(hash).replace(',', '.')) || 0) * f;
  }
  // scale: revenue multiplier (Bitcoin price chosen / current price), 1 by default
  function asicDay(data, algo, hash, unit, power, kwh, scale) {
    var a = data && data.algorithms && data.algorithms[algo];
    if (!a) return null;
    var h = hs(hash, unit);
    if (!(h > 0)) return null;
    var rev = a.eur_per_hs_day * h * (scale > 0 ? scale : 1);
    var cost = (parseFloat(power) || 0) / 1000 * 24 * kwh;
    return { revenue: rev, power: cost, net: rev - cost };
  }
  function gpuMonth(g, a, util, kwh) {
    var revenue = g.price_h.median * 24 * util * a.host_share * DAYS_PER_MONTH;
    var energy = g.power_w / 1000 * 24 * (util * a.load + (1 - util) * a.idle) * kwh * DAYS_PER_MONTH;
    return { revenue: revenue, power: energy, net: revenue - energy };
  }
  /* ---------- GPU models → shop products (model read from the product title, longest name first) ---------- */
  function norm(s) {
    return ' ' + String(s).toUpperCase().replace(/NVIDIA|GEFORCE|®|™/g, ' ').replace(/(\d)(TI|SUPER)\b/g, '$1 $2')
      .replace(/[^A-Z0-9]+/g, ' ').trim() + ' ';
  }
  function gpuAliases(g) {
    var n = norm(g.name).trim(), out = [n];
    if (/ SUPER$/.test(n)) out.push(n.replace(/ SUPER$/, 'S'));
    if (g.id === 'a100-pcie') out.push('A100');
    if (g.id === 'h100-sxm') out.push('H100');
    return out;
  }
  function cardCount(title) {
    var m = String(title).match(/(\d{1,2})\s*[x×]\s*/i) || String(title).match(/(\d{1,2})\s*GPU/i);
    return m ? parseInt(m[1], 10) || 1 : 1;
  }
  // { gpu id: url of the product with the fewest cards (a single card before an 8-GPU server) }
  function gpuProducts(gpus, items) {
    var list = [];
    gpus.forEach(function (g) { gpuAliases(g).forEach(function (a) { list.push([a, g.id]); }); });
    list.sort(function (a, b) { return b[0].length - a[0].length; });
    var map = {};
    items.forEach(function (it) {
      var t = norm(it.title);
      for (var i = 0; i < list.length; i++) {
        if (t.indexOf(' ' + list[i][0] + ' ') === -1) continue;
        var c = cardCount(it.title), cur = map[list[i][1]];
        if (!cur || c < cur.c) map[list[i][1]] = { url: it.url, c: c };
        break;
      }
    });
    return map;
  }

  /* ---------- global electricity price ---------- */
  var KEY = 'hm_kwh';
  function kwhUser() {
    try { var v = parseFloat(window.localStorage.getItem(KEY)); return v >= 0 && v <= 2 ? v : null; } catch (e) { return null; }
  }
  function kwhSet(v) {
    v = Math.round(parseFloat(v) * 10000) / 10000;
    try { if (v >= 0 && v <= 2) window.localStorage.setItem(KEY, String(v)); else window.localStorage.removeItem(KEY); } catch (e) { /* private mode: page only */ }
    document.dispatchEvent(new CustomEvent('hm:kwh', { detail: { kwh: v >= 0 && v <= 2 ? v : null } }));
  }
  function kwhReset() {
    try { window.localStorage.removeItem(KEY); } catch (e) { /* nothing stored */ }
    document.dispatchEvent(new CustomEvent('hm:kwh', { detail: { kwh: null } }));
  }
  window.hmKwh = { get: kwhUser, set: kwhSet, reset: kwhReset, open: function (el) { openPop(el); } };
  function kwhTxt(v) { return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 3 }).format(v); }
  // default price of the site (window.hmRent.kwh, 0 = no electricity deducted); 0 is a valid value, not a missing one
  function kwhBase(fb) { return typeof cfg.kwh === 'number' && cfg.kwh >= 0 ? cfg.kwh : fb; }
  function asicKwh(data) { var u = kwhUser(); return u !== null ? u : kwhBase((data && data.assumptions && data.assumptions.kwh_eur) || 0.08); }

  /* ---------- Bitcoin price chosen by the visitor ---------- */
  var BTC_KEY = 'hm_btc';
  function btcUser() {
    try { var v = parseFloat(window.localStorage.getItem(BTC_KEY)); return v > 0 ? v : null; } catch (e) { return null; }
  }
  // v > 0: price kept for the site; anything else: back to the default (all-time high)
  function btcSave(v) {
    v = Math.round(parseFloat(v));
    try { if (v > 0) window.localStorage.setItem(BTC_KEY, String(v)); else window.localStorage.removeItem(BTC_KEY); } catch (e) { /* private mode: page only */ }
    document.dispatchEvent(new CustomEvent('hm:btc', { detail: { btc: v > 0 ? v : null } }));
  }
  function btcAth(data) { return (data && (data.btc_eur_ath || data.btc_eur)) || 0; }
  function btcDefault(data) { var u = btcUser(); return u !== null ? u : btcAth(data); }
  window.hmBtc = { get: btcUser, set: btcSave, ath: btcAth };

  // our hosting electricity price ($/kWh) in euros, with the rate of the GPU data file
  function hostingEur() {
    return load(cfg.gpu).then(function (d) { return (cfg.hostingUsd || 0.07) * (d.usd_eur || 0.86); }, function () { return (cfg.hostingUsd || 0.07) * 0.86; });
  }

  /* ---------- pop-up « your electricity price » ---------- */
  var pop = null;
  function buildPop() {
    pop = document.createElement('div');
    pop.className = 'hm-kwh-pop';
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-labelledby', 'hm-kwh-pop-title');
    pop.hidden = true;
    pop.innerHTML = '<div class="hm-kwh-pop__head"><strong id="hm-kwh-pop-title">' + esc(t('kwh_title')) + '</strong><button type="button" class="hm-kwh-pop__x" aria-label="' + esc(t('close')) + '">×</button></div>' +
      '<label class="hm-kwh-pop__field"><input type="number" min="0" max="2" step="0.01" inputmode="decimal"><span>€/kWh</span></label>' +
      '<div class="hm-kwh-pop__chips">' + [0.05, 0.08, 0.15, 0.25].map(function (v) { return '<button type="button" data-v="' + v + '">' + kwhTxt(v) + ' €</button>'; }).join('') +
      '<button type="button" class="is-hosting" data-hosting>' + esc(t('kwh_hosting')) + '</button></div>' +
      '<p class="hm-kwh-pop__note">' + esc(t('kwh_note')) + '</p>' +
      '<div class="hm-kwh-pop__actions"><button type="button" class="hm-kwh-pop__reset">' + esc(t('kwh_reset')) + '</button><button type="button" class="hm-kwh-pop__apply">' + esc(t('kwh_apply')) + '</button></div>';
    document.body.appendChild(pop);
    var input = pop.querySelector('input');
    pop.querySelector('.hm-kwh-pop__x').addEventListener('click', closePop);
    pop.querySelectorAll('[data-v]').forEach(function (b) { b.addEventListener('click', function () { input.value = b.getAttribute('data-v'); }); });
    pop.querySelector('[data-hosting]').addEventListener('click', function () { hostingEur().then(function (v) { input.value = (Math.round(v * 1000) / 1000).toFixed(3); }); });
    pop.querySelector('.hm-kwh-pop__reset').addEventListener('click', function () { kwhReset(); closePop(); });
    pop.querySelector('.hm-kwh-pop__apply').addEventListener('click', function () {
      var v = parseFloat(String(input.value).replace(',', '.'));
      if (v >= 0 && v <= 2) kwhSet(v);
      closePop();
    });
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); pop.querySelector('.hm-kwh-pop__apply').click(); } });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !pop.hidden) closePop(); });
    document.addEventListener('click', function (e) { if (!pop.hidden && !pop.contains(e.target) && !e.target.closest('[data-hm-kwh-edit]')) closePop(); });
  }
  var lastOpener = null;
  function openPop(el) {
    if (!pop) buildPop();
    lastOpener = el || null;
    pop.querySelector('input').value = asicKwh(null).toFixed(2);
    pop.hidden = false;
    var r = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    var w = pop.offsetWidth, h = pop.offsetHeight, vw = window.innerWidth, vh = window.innerHeight;
    var left = r ? Math.min(Math.max(12, r.left), vw - w - 12) : (vw - w) / 2;
    var top = r ? (r.bottom + 8 + h < vh ? r.bottom + 8 : Math.max(12, r.top - h - 8)) : (vh - h) / 2;
    pop.style.left = left + 'px';
    pop.style.top = top + 'px';
    pop.querySelector('input').focus();
  }
  function closePop() {
    if (!pop || pop.hidden) return;
    pop.hidden = true;
    if (lastOpener && lastOpener.focus) lastOpener.focus();
  }
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-hm-kwh-edit]');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    openPop(b);
  }, true);

  /* ---------- product cards ---------- */
  function fillCard(el, data, kwh) {
    var out = el.querySelector('[data-hm-profit]');
    var d = asicDay(data, el.getAttribute('data-hm-algo'), el.getAttribute('data-hm-hash'), el.getAttribute('data-hm-hash-unit'), el.getAttribute('data-hm-power'), kwh);
    if (!out || !d) return;
    // the whole line opens the electricity price pop-up; the price used is written at the end of the line
    var line = i18n.card_line ? t('card_line', { x: money(d.net, eur2), kwh: '<span class="hm-cs__kwh">' + kwhTxt(kwh) + '</span>' }) : t('card_net', { x: money(d.net, eur2) });
    out.innerHTML = '<button type="button" class="hm-cs__profit-btn" data-hm-kwh-edit aria-label="' + esc(t('kwh_edit')) + '">' + line + '</button>';
    out.title = t(kwhUser() === null && i18n.card_title_default ? 'card_title_default' : 'card_title', { rev: eur2.format(d.revenue), kwh: kwhTxt(kwh) });
    out.hidden = false;
  }
  function fillCards(root, all) {
    var cards = (root || document).querySelectorAll(all ? '.hm-card-specs[data-hm-algo]' : '.hm-card-specs[data-hm-algo]:not([data-hm-rent])');
    if (!cards.length) return;
    load(cfg.asic).then(function (data) {
      var kwh = asicKwh(data);
      cards.forEach(function (el) { el.setAttribute('data-hm-rent', ''); fillCard(el, data, kwh); });
    }).catch(function () { /* figures stay hidden */ });
  }
  document.addEventListener('hm:kwh', function () { fillCards(document, true); });

  /* ---------- home miners: chance of finding a Bitcoin block in solo mining ---------- */
  // Live network figures: btc-network.json, published every 10 minutes by the Prefect flow « Bitcoin Network » next
  // to asic.json. Each hash has a 1 / (difficulty x 2^32) chance of being a valid block, so a hashrate H finds
  // H x blocks_per_hs_day blocks per day (blocks_per_hs_day = 86400 / (difficulty x 2^32)): the odds follow every
  // difficulty adjustment. Chance of at least one block in d days = 1 - e^(-blocks per day x d), written « 1 in N »
  // (data-tpl, __N__). Block reward = subsidy + average fees of the last blocks, in euros at the current price.
  // Fallback when the file is missing: asic.json (BTC per H/s per day of SHA-256 / reward of 3.15 BTC).
  var BLOCK_BTC = 3.15; // fallback only: 3.125 BTC subsidy since the April 2024 halving + average fees
  var NET_URL = cfg.net || String(cfg.asic || '').replace(/asic\.json(?=$|\?)/, 'btc-network.json');
  var NET_TPL = {
    fr: 'Réseau Bitcoin en direct : {hr} · difficulté {d} · un bloc toutes les {bt} · prochain ajustement ≈ {chg} {eta}',
    de: 'Bitcoin-Netzwerk live: {hr} · Schwierigkeit {d} · ein Block alle {bt} · nächste Anpassung ≈ {chg} {eta}',
    es: 'Red Bitcoin en directo: {hr} · dificultad {d} · un bloque cada {bt} · próximo ajuste ≈ {chg} {eta}',
    it: 'Rete Bitcoin in diretta: {hr} · difficoltà {d} · un blocco ogni {bt} · prossimo aggiustamento ≈ {chg} {eta}',
    nl: 'Bitcoin-netwerk live: {hr} · moeilijkheid {d} · elke {bt} een blok · volgende aanpassing ≈ {chg} {eta}',
    pt: 'Rede Bitcoin em direto: {hr} · dificuldade {d} · um bloco a cada {bt} · próximo ajuste ≈ {chg} {eta}',
    en: 'Live Bitcoin network: {hr} · difficulty {d} · one block every {bt} · next adjustment ≈ {chg} {eta}'
  };
  function network() {
    return load(NET_URL).then(function (n) {
      var perHs = parseFloat(n && n.blocks_per_hs_day), reward = parseFloat(n && n.reward_btc), btc = parseFloat(n && n.btc_eur);
      if (!(perHs > 0) || !(reward > 0) || !(btc > 0)) throw new Error('no network data');
      return { perHs: perHs, reward: reward, btc: btc, net: n };
    }).catch(function () {
      return load(cfg.asic).then(function (data) {
        var a = data && data.algorithms && data.algorithms['SHA-256'], btc = parseFloat(data && data.btc_eur);
        if (!a || !(a.eur_per_hs_day > 0) || !(btc > 0)) throw new Error('no SHA-256 data');
        return { perHs: a.eur_per_hs_day / btc / BLOCK_BTC, reward: BLOCK_BTC, btc: btc, net: null };
      });
    });
  }
  // one discreet line under the odds tiles of the product pages: the network figures the odds come from
  function netLine(n) {
    var lang = String(locale).slice(0, 2).toLowerCase(), tpl = NET_TPL[lang] || NET_TPL.en;
    var n0 = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }), n1 = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
    var bt = Math.round(parseFloat(n.block_time_s) || 600), r = n.retarget || {}, eta = '';
    var chg = parseFloat(r.change_pct), days = r.eta ? Math.max(0, Math.round((Date.parse(r.eta) - Date.now()) / 864e5)) : NaN;
    if (!(n.hashrate_hs > 0) || !(n.difficulty > 0) || isNaN(chg)) return '';
    try { if (!isNaN(days)) eta = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(days, 'day'); } catch (e) { eta = ''; }
    return tpl.replace('{hr}', n0.format(n.hashrate_hs / 1e18) + ' EH/s')
      .replace('{d}', n1.format(n.difficulty / 1e12) + ' T')
      .replace('{bt}', Math.floor(bt / 60) + ' min ' + (bt % 60) + ' s')
      .replace('{chg}', new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1, signDisplay: 'exceptZero' }).format(chg / 100))
      .replace(' {eta}', eta ? ' ' + eta : '');
  }
  function fillOdds(root) {
    var els = (root || document).querySelectorAll('[data-hm-odds]:not([data-ready]), [data-hm-reward-eur]:not([data-ready])');
    if (!els.length) return;
    els.forEach(function (el) { el.setAttribute('data-ready', ''); });
    network().then(function (k) {
      var n2 = new Intl.NumberFormat(locale, { maximumSignificantDigits: 2 });
      var pct = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 });
      els.forEach(function (el) {
        if (el.hasAttribute('data-hm-reward-eur')) { el.textContent = '≈ ' + eur0.format(k.reward * k.btc); el.hidden = false; return; }
        var lambda = hs(el.getAttribute('data-hash'), el.getAttribute('data-unit')) * k.perHs * (parseFloat(el.getAttribute('data-days')) || 1);
        if (!(lambda > 0)) return;
        var p = 1 - Math.exp(-lambda);
        el.textContent = p >= 0.5 ? pct.format(p) : String(el.getAttribute('data-tpl') || '1 / __N__').replace('__N__', n2.format(1 / p));
      });
      var line = k.net ? netLine(k.net) : '';
      if (!line) return;
      els.forEach(function (el) {
        var box = el.closest('[data-hm-odds-box]');
        if (!box || !box.querySelector('dl') || box.querySelector('.hm-net')) return;
        var p = document.createElement('p');
        p.className = 'hm-net';
        p.setAttribute('data-nosnippet', '');
        p.style.cssText = 'margin:12px 0 0;font-size:13px;line-height:1.5;opacity:.72';
        p.textContent = line;
        box.appendChild(p);
      });
    }).catch(function () {
      els.forEach(function (el) { var box = el.closest('[data-hm-odds-box]'); if (box) box.hidden = true; });
    });
  }

  /* ---------- ASIC product page ---------- */
  function initProduct(root) {
    var form = root.querySelector('form');
    var kwhInput = form.querySelector('[name="kwh"]'), countInput = form.querySelector('[name="count"]');
    var out = root.querySelector('[data-out="body"]');
    var updated = root.querySelector('[data-out="updated"]');
    var btcInput = form.querySelector('[name="btc"]'); // Bitcoin miners only
    var data = null;
    function btcTyped() { return btcInput ? parseFloat(String(btcInput.value).replace(',', '.')) : NaN; }
    // revenue multiplier: Bitcoin price typed / current price (1 without the field or a valid price)
    function btcScale() {
      var v = btcTyped();
      return btcInput && data && data.btc_eur > 0 && v > 0 ? v / data.btc_eur : 1;
    }
    function btcTarget(kind) { return Math.round(kind === 'ath' ? btcAth(data) : (data && data.btc_eur) || 0); }
    function btcChips() {
      if (!btcInput || !data) return;
      var v = Math.round(btcTyped());
      root.querySelectorAll('[data-btc-set]').forEach(function (b) {
        b.setAttribute('aria-pressed', v === btcTarget(b.getAttribute('data-btc-set')) ? 'true' : 'false');
      });
    }
    function render() {
      var kwh = parseFloat(String(kwhInput.value).replace(',', '.'));
      if (!(kwh >= 0)) kwh = asicKwh(data);
      var n = Math.max(1, parseInt(countInput.value, 10) || 1);
      var d = asicDay(data, root.getAttribute('data-algo'), root.getAttribute('data-hash'), root.getAttribute('data-unit'), root.getAttribute('data-power'), kwh, btcScale());
      btcChips();
      if (!d) return;
      var price = parseFloat(root.getAttribute('data-price'));
      var euro = root.getAttribute('data-currency') === 'EUR';
      var month = d.net * n * DAYS_PER_MONTH;
      var payback = euro && price > 0 && d.net > 0 ? Math.ceil(price / (d.net * DAYS_PER_MONTH)) : null;
      out.innerHTML = '<dl class="hm-rent-kpis">' +
        '<div><dt>' + esc(t('revenue_day')) + '</dt><dd>' + eur2.format(d.revenue * n) + '<small>' + esc(t('per_day')) + '</small></dd></div>' +
        '<div><dt>' + esc(t('power_day')) + '</dt><dd>' + eur2.format(d.power * n) + '<small>' + esc(t('per_day')) + '</small></dd></div>' +
        '<div><dt>' + esc(t('net_day')) + '</dt><dd>' + money(d.net * n, eur2) + '<small>' + esc(t('per_day')) + '</small></dd></div>' +
        '<div><dt>' + esc(t('net_month')) + '</dt><dd>' + money(month) + '<small>' + esc(t('per_month')) + '</small></dd></div>' +
        (euro && price > 0 ? '<div><dt>' + esc(t('payback')) + '</dt><dd>' + (payback ? payback + ' ' + esc(t('months')) : '—') + '<small>' + (payback ? esc(t('per_machine')) : esc(t('no_payback'))) + '</small></dd></div>' : '') +
        '</dl>';
    }
    load(cfg.asic).then(function (d) {
      data = d;
      var a = d.algorithms && d.algorithms[root.getAttribute('data-algo')];
      if (!a) { root.hidden = true; return; } // algorithm missing from today's data
      if (!kwhInput.hasAttribute('data-fixed') || kwhUser() !== null) kwhInput.value = asicKwh(d).toFixed(2);
      if (btcInput) {
        root.querySelectorAll('[data-btc-v]').forEach(function (el) { el.textContent = eur0.format(btcTarget(el.getAttribute('data-btc-v'))); });
        if (!(btcTyped() > 0)) btcInput.value = Math.round(btcDefault(d)) || '';
      }
      if (updated && d.updated_at) updated.textContent = t('updated', { date: new Date(d.updated_at).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) });
      render();
    }).catch(function () { root.hidden = true; });
    form.addEventListener('input', render);
    if (btcInput) {
      // a typed price is kept for the site; « ATH » goes back to the default, « current price » keeps today's price
      btcInput.addEventListener('change', function () { var v = btcTyped(); if (v > 0) btcSave(v); });
      root.querySelectorAll('[data-btc-set]').forEach(function (b) {
        b.addEventListener('click', function () {
          if (!data) return;
          var kind = b.getAttribute('data-btc-set');
          btcInput.value = btcTarget(kind) || '';
          btcSave(kind === 'ath' ? null : btcTarget(kind));
          render();
        });
      });
      document.addEventListener('hm:btc', function () {
        if (!data) return;
        var v = Math.round(btcDefault(data));
        if (v > 0 && Math.round(btcTyped()) !== v) { btcInput.value = v; render(); }
      });
    }
    // the visitor's price becomes the price of the whole site
    kwhInput.addEventListener('change', function () { var v = parseFloat(String(kwhInput.value).replace(',', '.')); if (v >= 0 && v <= 2) kwhSet(v); });
    document.addEventListener('hm:kwh', function () {
      if (!data) return;
      var v = asicKwh(data);
      if (Math.abs((parseFloat(String(kwhInput.value).replace(',', '.')) || 0) - v) > 1e-6) kwhInput.value = v.toFixed(2);
      render();
    });
  }

  /* ---------- home page ---------- */
  function initHome(root) {
    var nA = parseInt(root.getAttribute('data-asic-limit'), 10) || 6;
    var nG = parseInt(root.getAttribute('data-gpu-limit'), 10) || 6;
    var outA = root.querySelector('[data-out="asic"]'), outG = root.querySelector('[data-out="gpu"]');
    var gpuPage = root.getAttribute('data-gpu-page') || '/pages/rentabilite-gpu';
    var gpuItems = [].slice.call(root.querySelectorAll('[data-hm-gpu-product]')).map(function (li) { return { title: li.getAttribute('data-title'), url: li.getAttribute('data-url') }; });
    function drawAsic() { return load(cfg.asic).then(function (data) {
      var kwh = asicKwh(data);
      var rows = [].slice.call(root.querySelectorAll('[data-hm-asic-item]')).map(function (li) {
        var d = asicDay(data, li.getAttribute('data-algo'), li.getAttribute('data-hash'), li.getAttribute('data-unit'), li.getAttribute('data-power'), kwh);
        return d && { title: li.getAttribute('data-title'), url: li.getAttribute('data-url'), algo: li.getAttribute('data-algo'), d: d };
      }).filter(Boolean).sort(function (x, y) { return y.d.net - x.d.net; }).slice(0, nA);
      if (!rows.length) throw new Error('empty');
      outA.innerHTML = '<table class="hm-rent-table"><caption>' + esc(t(kwhUser() === null && i18n.asic_caption_default ? 'asic_caption_default' : 'asic_caption', { kwh: '\u0000' })).replace('\u0000', '<button type="button" class="hm-kwh-btn" data-hm-kwh-edit aria-label="' + esc(t('kwh_edit')) + '">' + kwhTxt(kwh) + '</button>') + '</caption><thead><tr><th scope="col">' + esc(t('col_miner')) + '</th><th scope="col">' + esc(t('col_revenue')) + '</th><th scope="col">' + esc(t('col_net')) + '</th></tr></thead><tbody>' +
        rows.map(function (r) {
          return '<tr><th scope="row"><a href="' + esc(r.url) + '">' + esc(r.title) + '</a><small>' + esc(r.algo) + '</small></th><td>' + eur2.format(r.d.revenue) + '</td><td><b>' + money(r.d.net, eur2) + '</b></td></tr>';
        }).join('') + '</tbody></table>';
    }).catch(function () { outA.innerHTML = '<p class="hm-rent-status">' + esc(t('error')) + '</p>'; }); }
    function drawGpu() { return load(cfg.gpu).then(function (data) {
      var a = data.assumptions || {};
      var prod = gpuProducts(data.gpus, gpuItems);
      var gk = kwhUser() !== null ? kwhUser() : kwhBase(a.kwh_eur || 0.2);
      // half the rows for the shop's GPUs, the rest for the most profitable models of the market (B300, B200, H200…)
      var all = data.gpus.map(function (g) { return { g: g, p: prod[g.id], m: gpuMonth(g, a, a.utilization || 0.6, gk) }; })
        .sort(function (x, y) { return y.m.net - x.m.net; });
      all.forEach(function (r) { r.shop = !!r.p; }); // « in the shop » = an active product matches the model
      var shop = all.filter(function (r) { return r.shop; }).slice(0, Math.ceil(nG / 2));
      var market = all.filter(function (r) { return !r.shop; }).slice(0, nG - shop.length);
      var rows = market.concat(shop).sort(function (x, y) { return y.m.net - x.m.net; });
      outG.innerHTML = '<table class="hm-rent-table"><caption>' + esc(t('gpu_caption', { util: Math.round((a.utilization || 0.6) * 100), kwh: '\u0000' })).replace('\u0000', '<button type="button" class="hm-kwh-btn" data-hm-kwh-edit aria-label="' + esc(t('kwh_edit')) + '">' + kwhTxt(gk) + '</button>') + '</caption><thead><tr><th scope="col">' + esc(t('col_gpu')) + '</th><th scope="col">' + esc(t('col_revenue_month')) + '</th><th scope="col">' + esc(t('col_net_month')) + '</th></tr></thead><tbody>' +
        rows.map(function (r) {
          // the GPU links to its product in the shop, otherwise to the GPU calculator with the model selected
          var name = r.p ? '<a href="' + esc(r.p.url) + '">' + esc(r.g.name) + '</a>'
            : '<a class="hm-rent-calc" href="' + esc(gpuPage + (gpuPage.indexOf('?') === -1 ? '?' : '&') + 'gpu=' + encodeURIComponent(r.g.id)) + '">' + esc(r.g.name) + '</a>';
          return '<tr><th scope="row">' + name + (r.shop ? '<span class="hm-rent-badge">' + esc(t('in_shop')) + '</span>' : '') + '<small>' + r.g.vram_gb + ' ' + esc(t('gb')) + '</small></th><td>' + eur0.format(r.m.revenue) + '</td><td><b>' + money(r.m.net) + '</b></td></tr>';
        }).join('') + '</tbody></table>';
    }).catch(function () { outG.innerHTML = '<p class="hm-rent-status">' + esc(t('error')) + '</p>'; }); }
    drawAsic();
    drawGpu();
    document.addEventListener('hm:kwh', function () { drawAsic(); drawGpu(); });
  }

  function boot() {
    fillCards(document);
    fillOdds(document);
    document.querySelectorAll('[data-hm-asic]:not([data-ready])').forEach(function (el) { el.setAttribute('data-ready', ''); initProduct(el); });
    document.querySelectorAll('[data-hm-rent-home]:not([data-ready])').forEach(function (el) { el.setAttribute('data-ready', ''); initHome(el); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  // cards added later (collection filters, infinite scroll, quick search)
  var pending = null;
  new MutationObserver(function () {
    if (pending) return;
    pending = setTimeout(function () { pending = null; fillCards(document); fillOdds(document); }, 300);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
